import React, { useMemo } from "react";
import { withAuthenticationRequired, useAuth0 } from "@auth0/auth0-react";
import { store } from "../store";
import { useTranslation } from "react-i18next";
import {
  Grid,
  Stack,
  Button,
  Typography,
  Box,
  CircularProgress,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
} from "@mui/material";
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import UsageMetric from "../components/cards/UsageMetric";
import LastCheckin from "../components/cards/LastCheckin";
import DeviceGraph from "../components/cards/DeviceGraph";
import TriphaseGraph from "../components/cards/TriphaseGraph";
import DatePickerDialog from "../components/dialogs/DatePicker";
import SelectResidence from "../components/selectResidence";
import { People, Home, CalendarViewDay, CalendarViewWeek, CalendarViewMonth, ChevronLeft, ChevronRight, Download, EvStation, Sms, Whatshot, Bolt } from "@mui/icons-material";
import DeviceTypeIcon from "../components/residence/DeviceTypeIcon";
import fetchResidenceUsage from "../functions/fetchResidenceUsage";
import fetchScreens from "../functions/fetchScreens";
import moment from "moment-timezone";
import { CONFIG } from "../config";
import { useTheme } from "@emotion/react";
import logger from "../utils/logger";
import SmsAlertsCard from "../components/cards/SmsAlertsCard";
import IndexReadingsSection from "../components/residence-usage/IndexReadingsSection";
import AlertThresholdsSection from "../components/residence-usage/AlertThresholdsSection";
import TemperatureThresholdsSection from "../components/residence-usage/TemperatureThresholdsSection";
import CalibrationSection from "../components/residence-usage/CalibrationSection";
import { getDeviceThresholds } from "../API/deviceThresholds";
import PlugyChargingStations from "../components/residence-usage/PlugyChargingStations";
import AddIndexReadingDialog from "../components/dialogs/AddIndexReading";
import EditIndexReadingDialog from "../components/dialogs/EditIndexReading";
import fetchIndexReadings from "../functions/fetchIndexReadings";
// Note: IndexReadingsTable est importé dans IndexReadingsSection


// Fonction pour traduire les unités de l'API
const translateUnit = (unit) => {
  if (unit === "liters") return "litres";
  return unit; // Garde kWh et autres unités telles quelles
};

// Fonction pour convertir le type de température en texte lisible
const getTemperatureTypeLabel = (tempType) => {
  switch(tempType) {
    case "chambre_froide_positive":
      return "Chambre froide positive";
    case "chambre_froide_negative":
      return "Chambre froide négative";
    case "ambiante":
      return "Température ambiante";
    default:
      return tempType || "Température";
  }
};

function ResidenceUsage(props) {
  const { t } = useTranslation("common");
  const { getAccessTokenSilently } = useAuth0();
  const { id } = useParams();
  const userDB = useSelector((state) => state.userDB);
  const screens = useSelector((state) => state.screens);
  const residencesFromStore = useSelector((state) => state.residences || []);
  const establishmentsFromStore = useSelector((state) => state.establishments || []);
  const theme = useTheme();

  // Get day_start_hour from Redux store (available immediately if data is cached)
  const dayStartHourFromStore = useMemo(() => {
    if (!id || !residencesFromStore.length || !establishmentsFromStore.length) return 0;
    const res = residencesFromStore.find(r => r.id === parseInt(id));
    if (!res) return 0;
    const est = establishmentsFromStore.find(e => e.id === res.establishment_id);
    return est?.day_start_hour || 0;
  }, [id, residencesFromStore, establishmentsFromStore]);

  // Initialize dates with day_start_hour offset if available from store
  // If current time is BEFORE dayStartHour, we're still in "yesterday's" day
  const getInitialStartDate = () => {
    const hour = dayStartHourFromStore || 0;
    const now = moment().tz("Europe/Paris");
    const currentHour = now.hour();

    // If we're before the day start hour, go back one day
    if (hour > 0 && currentHour < hour) {
      return now.clone().subtract(1, "day").startOf("day").add(hour, "hours");
    }
    return now.clone().startOf("day").add(hour, "hours");
  };

  const getInitialEndDate = () => {
    const hour = dayStartHourFromStore || 0;
    const now = moment().tz("Europe/Paris");
    const currentHour = now.hour();

    if (hour > 0) {
      // If we're before the day start hour, end date is today at dayStartHour
      if (currentHour < hour) {
        return now.clone().startOf("day").add(hour, "hours").subtract(1, "millisecond");
      }
      // Otherwise, end date is tomorrow at dayStartHour
      return now.clone().startOf("day").add(1, "day").add(hour, "hours").subtract(1, "millisecond");
    }
    return now.clone().endOf("day");
  };

  const [startDate, setStartDate] = useState(getInitialStartDate);

  const [endDate, setEndDate] = useState(getInitialEndDate);
  const [dateType, setDateType] = useState("day");
  const [grouped_by, setGroupedBy] = useState("hourly");
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 900);

  // State to store the data from the comprehensive endpoint
  const [usageData, setUsageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [deviceSmsStates, setDeviceSmsStates] = useState({});
  const [deviceTemperatureTypes, setDeviceTemperatureTypes] = useState({});
  const [deviceThresholds, setDeviceThresholds] = useState({});
  const [devicePauseStates, setDevicePauseStates] = useState({});
  // Seuils de COLORATION température (jaune/rouge) par capteur, indépendants du SMS.
  // { [dev_eui]: { yellow, red, temperature_type } }
  const [deviceTempThresholds, setDeviceTempThresholds] = useState({});
  // Bumpé après un étalonnage : force le refetch pour que cartes et graphes
  // repartent sur la valeur corrigée sans attendre le rafraîchissement auto.
  const [calibrationVersion, setCalibrationVersion] = useState(0);

  // Autorisation SMS : true uniquement si l'admin a configuré AlertConfig (enabled + au moins 1 contact)
  const [smsAllowed, setSmsAllowed] = useState(false);

  // Index readings state
  const [showAddIndexReadingDialog, setShowAddIndexReadingDialog] = useState(false);
  const [showEditIndexReadingDialog, setShowEditIndexReadingDialog] = useState(false);
  const [selectedDeviceForReading, setSelectedDeviceForReading] = useState(null);
  const [selectedReadingToEdit, setSelectedReadingToEdit] = useState(null);
  const indexReadings = useSelector((state) => state.indexReadings);

  const navigate = useNavigate();

  // Fetch yesterday's data for comparison
  const [yesterdayData, setYesterdayData] = useState(null);

  // État optimiste des toggles ADE_DRY associés aux cartes UC300 ({channelNum: 0|1})
  const [adeDryStates, setAdeDryStates] = useState({});

  // Map API's snake_case properties to our camelCase variables - MUST be before useEffects that use them
  // dev_eui des devices Plugy (borne IRVE) : parent brand 88, canaux 89..96.
  // Rendus dans une section dédiée (PlugyChargingStations) -> exclus du rendu
  // générique pour éviter le doublon (cartes/graphes électriques classiques).
  const plugyEuis = useMemo(() => {
    const s = new Set();
    Object.values((usageData && usageData.devices_by_type) || {}).forEach((list) =>
      (list || []).forEach((d) => {
        if (d.device_brand_id === 88 || (d.device_brand_id >= 89 && d.device_brand_id <= 96))
          s.add(d.dev_eui);
      })
    );
    return s;
  }, [usageData]);
  const devicesByType = useMemo(() => {
    const src = (usageData && usageData.devices_by_type) || {};
    const out = {};
    Object.entries(src).forEach(([t, list]) => {
      const filtered = (list || []).filter((d) => !plugyEuis.has(d.dev_eui));
      if (filtered.length) out[t] = filtered;
    });
    return out;
  }, [usageData, plugyEuis]);
  const establishment = useMemo(
    () => (usageData && usageData.establishment) || {},
    [usageData]
  );
  const residence = useMemo(
    () => (usageData && usageData.residence) || {},
    [usageData]
  );
  const deviceTypeCategories = useMemo(() => {
    const src = (usageData && usageData.device_type_categories) || {};
    const out = {};
    Object.entries(src).forEach(([t, cat]) => {
      const devices = (cat.devices || []).filter((e) => !plugyEuis.has(e));
      // On garde la catégorie même vide si elle n'était PAS 100% Plugy (mixte),
      // sinon on la retire pour ne pas afficher une carte électrique fantôme.
      if (devices.length || (cat.devices || []).length === 0) out[t] = { ...cat, devices };
    });
    return out;
  }, [usageData, plugyEuis]);
  const deviceHistories = useMemo(
    () => (usageData && usageData.device_histories) || {},
    [usageData]
  );
  const deviceTypes = useMemo(
    () => (usageData && usageData.device_types) || [],
    [usageData]
  );
  const deviceLimits = useMemo(
    () => (usageData && usageData.device_limits) || {},
    [usageData]
  );
  const dayStartHour = useMemo(
    () => (establishment && establishment.day_start_hour) || 0,
    [establishment]
  );
  const deviceTotals = useMemo(
    () => (usageData && usageData.device_totals) || {},
    [usageData]
  );
  const totalDays = useMemo(
    () =>
      (usageData && usageData.time_range && usageData.time_range.total_days) ||
      1,
    [usageData]
  );
  const totalPersons = useMemo(
    () => (residence && residence.current_occupation) || 1,
    [residence]
  );

  const hasWaterDevices = useMemo(
    () => Object.entries(devicesByType).some(([typeId, devices]) => {
      if (!devices?.length) return false;
      const dt = deviceTypes.find((d) => d.id === parseInt(typeId));
      return dt?.name?.toLowerCase().includes("water");
    }),
    [devicesByType, deviceTypes]
  );
  const hasElecDevices = useMemo(
    () => Object.entries(devicesByType).some(([typeId, devices]) => {
      if (!devices?.length) return false;
      const dt = deviceTypes.find((d) => d.id === parseInt(typeId));
      return dt?.name?.toLowerCase().includes("electric");
    }),
    [devicesByType, deviceTypes]
  );

  // Liste des capteurs de température (hors canaux AM103 humidité _CH3 / CO2 _CH4)
  // pour le réglage des seuils de coloration + la coloration carte/graphe.
  const temperatureDevicesList = useMemo(() => {
    const out = [];
    Object.entries(devicesByType).forEach(([typeId, devices]) => {
      const dt = deviceTypes.find((d) => d.id === parseInt(typeId));
      if (!dt?.name?.toLowerCase().includes("temp")) return;
      (devices || []).forEach((device) => {
        const name = device.name || "";
        if (name.includes("AM103") && (name.includes("_CH3") || name.includes("_CH4"))) return;
        out.push({ dev_eui: device.dev_eui, name: device.name });
      });
    });
    return out;
  }, [devicesByType, deviceTypes]);

  // Charger les seuils jaune/rouge de température pour chaque capteur (par device).
  const loadTempThresholds = useCallback(async () => {
    if (!temperatureDevicesList.length) return;
    const result = {};
    await Promise.all(
      temperatureDevicesList.map(async (dev) => {
        try {
          const resp = await getDeviceThresholds(dev.dev_eui);
          if (resp?.status === "success" && Array.isArray(resp.thresholds)) {
            const y = resp.thresholds.find((thr) => thr.threshold_type === "yellow");
            const r = resp.thresholds.find((thr) => thr.threshold_type === "red");
            result[dev.dev_eui] = {
              yellow: y ? y.threshold_value : null,
              red: r ? r.threshold_value : null,
              temperature_type: resp.thresholds[0]?.temperature_type || "ambiante",
            };
          }
        } catch (e) {
          // Erreur silencieuse : device sans seuils configurés
        }
      })
    );
    setDeviceTempThresholds(result);
  }, [temperatureDevicesList]);

  useEffect(() => {
    loadTempThresholds();
  }, [loadTempThresholds]);

  // Niveau de coloration température selon les seuils (identique au backend) :
  // valeur > rouge => "red", valeur > jaune => "yellow", sinon "green".
  const getTemperatureLevel = useCallback((temp, thr) => {
    if (temp == null || !thr) return null;
    if (thr.red != null && temp > thr.red) return "red";
    if (thr.yellow != null && temp > thr.yellow) return "yellow";
    return "green";
  }, []);

  // Détection des devices SDM630MCT triphasé (parent + 3 phases)
  const sdm630Config = useMemo(() => {
    let parentDevice = null;
    let parentTypeId = null;
    const phases = {};
    let phaseTypeId = null;
    const allTypeIds = new Set();

    for (const [typeId, devices] of Object.entries(devicesByType)) {
      for (const device of (devices || [])) {
        const name = (device.name || "").toLowerCase();
        const devEui = (device.dev_eui || "");

        const isSDM630 = name.includes("sdm630") || name.includes("triphas");
        if (!isSDM630) continue;

        allTypeIds.add(typeId);

        if (devEui.includes("_CH1")) {
          phases.l1 = { device, typeId, deviceId: device.dev_eui };
          phaseTypeId = typeId;
        } else if (devEui.includes("_CH2")) {
          phases.l2 = { device, typeId, deviceId: device.dev_eui };
          phaseTypeId = typeId;
        } else if (devEui.includes("_CH3")) {
          phases.l3 = { device, typeId, deviceId: device.dev_eui };
          phaseTypeId = typeId;
        } else {
          parentDevice = device;
          parentTypeId = typeId;
        }
      }
    }

    // Besoin d'au moins les 3 phases pour activer le mode triphasé
    if (!phases.l1 || !phases.l2 || !phases.l3) return null;

    return { parentDevice, parentTypeId, phases, phaseTypeId, allTypeIds };
  }, [devicesByType]);

  // Détection des canaux ADE_DRY virtuels présents dans le pack, indexés par
  // numéro de canal (1..4) — utilisé pour afficher un toggle ON/OFF dans les
  // cartes UC300 du même numéro de canal (CH1↔A, CH2↔B, CH3↔C, CH4↔D).
  // Source = même que le ResidenceDashboard : screen.device_data.usage_by_type
  // (clés ade_dry_A..D, refreshé par le setInterval refreshData global), pour
  // garantir que les deux écrans restent en phase.
  const adeDryByChannel = useMemo(() => {
    const result = {};
    if (!residence?.pack_id || !screens?.length) return result;
    const screen = screens.find((s) => s.pack_id === residence.pack_id);
    const usage = screen?.device_data?.usage_by_type || {};
    const letterToChannel = { A: 1, B: 2, C: 3, D: 4 };
    Object.entries(usage).forEach(([key, val]) => {
      if (!key.startsWith("ade_dry_")) return;
      const letter = key.split("_").pop();
      const channelNum = letterToChannel[letter];
      if (!channelNum) return;
      if (typeof val !== "object" || !val?.dev_eui) return;
      const rs = val.relay_state;
      const isOn = rs === 1 || rs === "ON" || rs === true || rs === 1.0;
      result[channelNum] = {
        parentDevEui: val.dev_eui,
        currentState: isOn ? 1 : 0,
      };
    });
    return result;
  }, [screens, residence]);

  useEffect(() => {
    async function fetchYesterdayData() {
      try {
        // Calculate yesterday's date range
        const yesterdayStart = moment(startDate).subtract(1, 'day');
        const yesterdayEnd = moment(endDate).subtract(1, 'day');
        
        const data = await fetchResidenceUsage(id, {
          startDate: yesterdayStart,
          endDate: yesterdayEnd,
          groupedBy: grouped_by,
        });
        
        setYesterdayData(data);
      } catch (error) {
        // Erreur silencieuse
      }
    }
    
    if (id) {
      fetchYesterdayData();
    }
  }, [id, startDate, endDate, grouped_by]);
  
  // Calculate yesterday's totals from the fetched data
  // Calculate yesterday's totals from the fetched data
  const yesterdayTotals = useMemo(() => {
    logger.log("=== Calculating yesterday totals ===");
    logger.log("yesterdayData:", yesterdayData);
    
    if (!yesterdayData || !yesterdayData.device_totals) {
      logger.log("No yesterday data available");
      return {};
    }
    
    // Get current hour in Europe/Paris timezone (not browser local time)
    const currentHour = moment().tz("Europe/Paris").hour();
    logger.log("Current hour (Paris):", currentHour);

    const totals = {};

    // If we're looking at today, find the data for the same hour yesterday
    if (moment(startDate).isSame(moment().tz("Europe/Paris"), 'day')) {
      logger.log("Looking at today, searching for same hour yesterday");

      Object.entries(yesterdayData.device_histories || {}).forEach(([typeId, devices]) => {
        logger.log(`Processing type ${typeId}:`, devices);
        let typeTotal = 0;

        Object.entries(devices).forEach(([deviceId, history]) => {
          logger.log(`Device ${deviceId} history length:`, history.length);

          // Find data for the same hour
          let cumulativeTotal = 0;

          history.forEach(item => {
            // Parse UTC timestamp and convert to Paris timezone for hour comparison
            const itemHour = moment.utc(item.time_created).tz("Europe/Paris").hour();

            // Include all hours from 0 to currentHour (inclusive)
            if (itemHour <= currentHour) {
              if (item.total_used !== undefined) {
                cumulativeTotal += item.total_used;
              } else if (item.total_liters !== undefined) {
                cumulativeTotal += item.total_liters;
              } else if (item.total_kwh !== undefined) {
                cumulativeTotal += item.total_kwh;
              } else if (item.temperature !== undefined) {
                cumulativeTotal = item.temperature; // Température: dernière valeur, pas cumul
              }
            }
          });
          
          logger.log(`Device ${deviceId} cumulative total up to hour ${currentHour}:`, cumulativeTotal);
          
          if (cumulativeTotal > 0) {
            typeTotal += cumulativeTotal;
            logger.log(`Added cumulative ${cumulativeTotal} units`);
          }
        });
        
        totals[typeId] = typeTotal;
        logger.log(`Total for type ${typeId}:`, typeTotal);
      });
    } else {
      logger.log("Not looking at today, using device_totals directly");
      return yesterdayData.device_totals || {};
    }
    
    logger.log("Final totals:", totals);
    return totals;
  }, [yesterdayData, startDate]);

  // Get alert colors from screens store
  const alertColors = useMemo(() => {
    if (!screens || !residence?.pack_id) return {};
    const screen = screens.find(s => s.pack_id === residence.pack_id);
    if (!screen) return {};
    return {
      water: screen.water_color || 'green',
      electricity: screen.electricity_color || 'green',
      temperature: screen.temperature_color || 'green',
      deviceColors: screen.device_data?.colors || {}
    };
  }, [screens, residence?.pack_id]);

  // Apply day_start_hour offset when data is available (from store or API)
  // Priority: dayStartHourFromStore (available earlier) > dayStartHour (from API)
  const [dayStartHourApplied, setDayStartHourApplied] = useState(false);
  const effectiveDayStartHour = dayStartHourFromStore || dayStartHour;

  // Reset dayStartHourApplied when residence changes
  useEffect(() => {
    setDayStartHourApplied(false);
  }, [id]);

  useEffect(() => {
    if (effectiveDayStartHour > 0 && !dayStartHourApplied && dateType === "day") {
      // Check if dates are already correctly set (avoid unnecessary updates)
      const expectedStartHour = startDate.hour();
      if (expectedStartHour !== effectiveDayStartHour) {
        // Apply the day_start_hour offset to the initial dates
        // If current time is BEFORE dayStartHour, we're still in "yesterday's" day
        const now = moment().tz("Europe/Paris");
        const currentHour = now.hour();

        let newStartDate, newEndDate;
        if (currentHour < effectiveDayStartHour) {
          // Before day start hour: show yesterday's day (yesterday noon to today noon)
          newStartDate = now.clone().subtract(1, "day").startOf("day").add(effectiveDayStartHour, "hours");
          newEndDate = now.clone().startOf("day").add(effectiveDayStartHour, "hours").subtract(1, "millisecond");
        } else {
          // After day start hour: show today's day (today noon to tomorrow noon)
          newStartDate = now.clone().startOf("day").add(effectiveDayStartHour, "hours");
          newEndDate = newStartDate.clone().add(1, "day").subtract(1, "millisecond");
        }
        setStartDate(newStartDate);
        setEndDate(newEndDate);
      }
      setDayStartHourApplied(true);
    }
  }, [effectiveDayStartHour, dayStartHourApplied, dateType, startDate]);

  // Sync dropdown with current residence from URL
  useEffect(() => {
    if (id && residence && residence.id) {
      store.dispatch({
        type: "selectedResidence/update",
        payload: residence.id,
      });
      localStorage.setItem("selectedResidence", residence.id);
    }
  }, [id, residence]);

  // Vérifier si l'admin a configuré les alertes SMS pour cet établissement
  useEffect(() => {
    async function checkSmsAllowed() {
      const establishmentId = establishment?.id;
      if (!establishmentId) {
        setSmsAllowed(false);
        return;
      }
      try {
        const token = await getAccessTokenSilently();
        const res = await fetch(
          `${CONFIG.API_URL}/api/alert-configs/establishment/${establishmentId}`,
          { headers: { "Authorization": `Bearer ${token}` } }
        );
        if (!res.ok) {
          setSmsAllowed(false);
          return;
        }
        const data = await res.json();
        const isEnabled = data.enabled === true || data.enabled === 1;
        const hasContacts = Array.isArray(data.sms_contacts) && data.sms_contacts.length > 0;
        setSmsAllowed(isEnabled && hasContacts);
      } catch (e) {
        setSmsAllowed(false);
      }
    }
    checkSmsAllowed();
  }, [establishment?.id, getAccessTokenSilently]);

  // Load SMS alert states for devices
  useEffect(() => {
    async function loadDeviceSmsStates() {
      if (!id || !residence || !smsAllowed) return;

      try {
        const token = await getAccessTokenSilently();
        const response = await fetch(
          `${CONFIG.API_URL}/residences/${id}/device-thresholds`,
          {
            headers: {
              "Authorization": `Bearer ${token}`,
            },
          }
        );

        if (response.ok) {
          const data = await response.json();
          const states = {};
          const types = {};
          const thresholds = {};
          const pauses = {};
          data.forEach(threshold => {
            if (threshold.threshold_type === 'red') {
              if (threshold.device_type === 'temperature') {
                types[threshold.device_dev_eui] = threshold.temperature_type;
                states[threshold.device_dev_eui] = threshold.alerts_sms || false;
                thresholds[threshold.device_dev_eui] = threshold.threshold_value;
                pauses[threshold.device_dev_eui] = threshold.sms_paused || false;
              } else if (threshold.device_type === 'water') {
                states[`water_${threshold.device_dev_eui}`] = threshold.alerts_sms || false;
                thresholds[`water_${threshold.device_dev_eui}`] = threshold.threshold_value;
                pauses[`water_${threshold.device_dev_eui}`] = threshold.sms_paused || false;
              }
            }
          });
          setDeviceTemperatureTypes(types);
          setDeviceSmsStates(states);
          setDeviceThresholds(thresholds);
          setDevicePauseStates(pauses);
        }
      } catch (error) {
        // Erreur silencieuse
      }
    }

    loadDeviceSmsStates();
  }, [id, residence, smsAllowed, getAccessTokenSilently]);

  // Load index readings for all devices in the residence
  useEffect(() => {
    async function loadIndexReadings() {
      if (!devicesByType) return;

      try {
        // Iterate through all devices and fetch their index readings
        Object.entries(devicesByType).forEach(([typeId, devices]) => {
          devices.forEach((device) => {
            fetchIndexReadings(device.dev_eui, 100);
          });
        });
      } catch (error) {
        console.error("Error loading index readings:", error);
      }
    }

    loadIndexReadings();
  }, [devicesByType]);

  // Toggle d'un canal ADE_DRY associé à une carte UC300.
  // channelNum ∈ [1..4], newState ∈ {0,1}. Mapping CH1↔A, CH2↔B, CH3↔C, CH4↔D
  // est fait côté backend par /api/adedry/{dev_eui}/command.
  const handleAdeDryToggle = useCallback(async (channelNum, newState) => {
    const entry = adeDryByChannel[channelNum];
    if (!entry) return;
    const channelLetter = ["A", "B", "C", "D"][channelNum - 1];

    // Optimistic UI
    setAdeDryStates((prev) => ({ ...prev, [channelNum]: newState }));

    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${CONFIG.API_URL}/api/adedry/${entry.parentDevEui}/command`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ channel: channelLetter, state: newState }),
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || err.detail || `HTTP ${response.status}`);
      }
      // Rafraîchir le store global pour que le ResidenceDashboard (qui lit
      // screen.device_data.usage_by_type) reflète immédiatement le nouvel état,
      // sans attendre le prochain tick du setInterval refreshData.
      fetchScreens().catch(() => { /* best-effort */ });
    } catch (err) {
      // Rollback
      setAdeDryStates((prev) => ({ ...prev, [channelNum]: newState ? 0 : 1 }));
      alert(`Erreur commande relais: ${err.message}`);
    }
  }, [adeDryByChannel, getAccessTokenSilently]);

  // Handle SMS toggle for a specific device
  const handleDeviceSmsToggle = async (deviceDevEui, currentState, deviceType = "temperature") => {
    try {
      const token = await getAccessTokenSilently();
      const newState = !currentState;
      
      const response = await fetch(
        `${CONFIG.API_URL}/residences/${id}/device/${deviceDevEui}/sms-alert`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ alerts_sms: newState, device_type: deviceType }),
        }
      );
      
      if (response.ok) {
        const stateKey = deviceType === 'water' ? `water_${deviceDevEui}` : deviceDevEui;
        setDeviceSmsStates(prev => ({
          ...prev,
          [stateKey]: newState
        }));
        logger.log(`SMS alerts ${newState ? 'enabled' : 'disabled'} for device ${deviceDevEui}`);
      } else {
        const error = await response.json();
        alert(error.detail || "Erreur lors de la modification");
      }
    } catch (error) {
      alert("Erreur lors de la modification");
    }
  };

  const handleDeviceSmsPause = async (deviceDevEui, deviceType, shouldPause) => {
    try {
      const token = await getAccessTokenSilently();
      const response = await fetch(
        `${CONFIG.API_URL}/residences/${id}/device/${deviceDevEui}/sms-pause`,
        {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify({ paused: shouldPause, device_type: deviceType }),
        }
      );
      if (response.ok) {
        const pauseKey = deviceType === 'water' ? `water_${deviceDevEui}` : deviceDevEui;
        setDevicePauseStates(prev => ({ ...prev, [pauseKey]: shouldPause }));
      }
    } catch (error) {
      // Erreur silencieuse
    }
  };

  // Handle threshold change for a specific device
  const handleThresholdChange = async (deviceDevEui, newValue, deviceType = "temperature") => {
    try {
      const token = await getAccessTokenSilently();
      const thresholdKey = deviceType === 'water' ? `water_${deviceDevEui}` : deviceDevEui;
      const unit = deviceType === 'water' ? 'liters' : 'celsius';
      const temperatureType = deviceType === 'temperature'
        ? (deviceTemperatureTypes[deviceDevEui] || 'ambiante')
        : 'water';

      const response = await fetch(
        `${CONFIG.API_URL}/api/device/${deviceDevEui}/thresholds/update`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`,
          },
          body: JSON.stringify([{
            temperature_type: temperatureType,
            threshold_type: "red",
            device_type: deviceType,
            threshold_value: parseFloat(newValue),
            unit: unit,
            alerts_email: true,
            alerts_sms: deviceSmsStates[thresholdKey] || false,
          }]),
        }
      );

      if (response.ok) {
        setDeviceThresholds(prev => ({
          ...prev,
          [thresholdKey]: parseFloat(newValue),
        }));
        return true;
      }
      return false;
    } catch (error) {
      logger.log("Error updating threshold:", error);
      return false;
    }
  };

  // Set up window resize listener in useEffect
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 900);
    };

    window.addEventListener("resize", handleResize);

    // Clean up the event listener when component unmounts
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  useEffect(() => {
    store.dispatch({
      type: "pageTitle/update",
      payload: t("common.residence_usage", {
        name: residence ? residence.name : "",
      }),
    });

    store.dispatch({
      type: "packId/update",
      payload: residence && residence.pack_id ? residence.pack_id : null,
    });
    store.dispatch({ type: "pageActions/update", payload: [] });
  }, [t, residence, startDate, endDate, dateType]);

  // Fetch data from the comprehensive endpoint
  useEffect(() => {
    //if the date range is more than 1 day, make sure we are set to group_by daily
    const diff = moment(endDate).diff(startDate, "days");

    if (diff > 1) {
      setGroupedBy("daily");
    } else {
      setGroupedBy("hourly");
    }
    async function fetchData() {
      // Only set loading to true during the initial load
      if (isInitialLoad) {
        setLoading(true);
      }
      try {
        const data = await fetchResidenceUsage(id, {
          startDate,
          endDate,
          groupedBy: grouped_by,
        });

        // Debug what we received
        logger.log("Residence usage data received:", data);

        // Check if the response contains an error
        if (data && data.error) {
          setError(data.message || "Failed to fetch residence usage data");
          setUsageData(null);
        } else {
          logger.log(
            "Setting usage data with device categories:",
            data.device_type_categories
          );
          logger.log(
            "Setting usage data with device totals:",
            data.device_totals
          );
          setUsageData(data);
          setError(null);
        }
      } catch (err) {
        setError(err.message || "Failed to fetch residence usage data");
      } finally {
        setLoading(false);
        setIsInitialLoad(false); // Set initial load to false after first fetch
      }
    }

    fetchData();

    // Set up an interval to refresh the data
    const interval = setInterval(() => {
      fetchData();
    }, CONFIG.defaultRefreshRate * 1000);

    return () => clearInterval(interval);
  }, [id, startDate, endDate, grouped_by, isInitialLoad, calibrationVersion]);

  function changePeriod(period) {
    let sd = moment(startDate);
    let ed = moment(endDate);

    // Helper function to apply day start hour offset for daily/weekly views
    // Takes into account whether we're before or after the day start hour
    const applyDayStartHourOffset = (m, isStart) => {
      if (effectiveDayStartHour > 0) {
        const currentHour = m.hour();
        if (isStart) {
          // If before day start hour, go back one day
          if (currentHour < effectiveDayStartHour) {
            return m.clone().subtract(1, "day").startOf("day").add(effectiveDayStartHour, "hours");
          }
          return m.clone().startOf("day").add(effectiveDayStartHour, "hours");
        } else {
          // End date: if before day start hour, end is today at day start hour
          if (currentHour < effectiveDayStartHour) {
            return m.clone().startOf("day").add(effectiveDayStartHour, "hours").subtract(1, "millisecond");
          }
          return m.clone().startOf("day").add(1, "day").add(effectiveDayStartHour, "hours").subtract(1, "millisecond");
        }
      }
      return isStart ? m.clone().startOf("day") : m.clone().endOf("day");
    };

    if (period === "minus") {
      if (dateType === "day") {
        sd = sd.subtract(1, "days");
        ed = ed.subtract(1, "days");
      } else if (dateType === "week") {
        sd = sd.subtract(1, "weeks");
        ed = ed.subtract(1, "weeks");
        if (effectiveDayStartHour > 0) {
          sd = sd.startOf("isoWeek").add(effectiveDayStartHour, "hours");
          ed = sd.clone().add(7, "days").subtract(1, "millisecond");
        } else {
          sd = sd.startOf("isoWeek");
          ed = ed.endOf("isoWeek");
        }
      } else if (dateType === "month") {
        sd = sd.subtract(1, "months");
        ed = ed.subtract(1, "months");
        sd = sd.startOf("month");
        ed = ed.endOf("month");
      }
    }

    if (period === "plus") {
      if (dateType === "day") {
        sd = sd.add(1, "days");
        ed = ed.add(1, "days");
      } else if (dateType === "week") {
        sd = sd.add(1, "weeks");
        ed = ed.add(1, "weeks");
        if (effectiveDayStartHour > 0) {
          sd = sd.startOf("isoWeek").add(effectiveDayStartHour, "hours");
          ed = sd.clone().add(7, "days").subtract(1, "millisecond");
        } else {
          sd = sd.startOf("isoWeek");
          ed = ed.endOf("isoWeek");
        }
      } else if (dateType === "month") {
        sd = sd.add(1, "months");
        ed = ed.add(1, "months");
        sd = sd.startOf("month");
        ed = ed.endOf("month");
      }
    }

    if (period === "day") {
      // Revenir à aujourd'hui quand on clique sur "Jour"
      // Apply day_start_hour offset for daily view
      sd = applyDayStartHourOffset(moment(), true);
      ed = applyDayStartHourOffset(moment(), false);
      setGroupedBy("hourly");
      setDateType("day");
    } else if (period === "week") {
      // Apply day_start_hour offset for weekly view
      if (effectiveDayStartHour > 0) {
        sd = startDate.clone().startOf("isoWeek").add(effectiveDayStartHour, "hours");
        ed = sd.clone().add(7, "days").subtract(1, "millisecond");
      } else {
        sd = startDate.clone().startOf("isoWeek");
        ed = startDate.clone().endOf("isoWeek");
      }
      setGroupedBy("daily");
      setDateType("week");
    } else if (period === "month") {
      // Monthly view stays at calendar standard (midnight)
      sd = startDate.clone().startOf("month");
      ed = startDate.clone().endOf("month");
      setGroupedBy("daily");
      setDateType("month");
    }

    setStartDate(sd);
    setEndDate(ed);

  }

  // Handler for clicking on a bar in week/month view to navigate to that specific day
  const handleBarClick = useCallback((clickedData) => {
    // Only navigate if we're in week or month view (grouped_by === "daily")
    if (grouped_by !== "daily") return;

    // Extract the date from the clicked bar
    const clickedDate = moment(clickedData.time_created).tz("Europe/Paris");

    // Set the date range to that specific day, respecting day_start_hour
    let newStartDate, newEndDate;
    if (effectiveDayStartHour > 0) {
      newStartDate = clickedDate.clone().startOf("day").add(effectiveDayStartHour, "hours");
      newEndDate = newStartDate.clone().add(1, "day").subtract(1, "millisecond");
    } else {
      newStartDate = clickedDate.clone().startOf("day");
      newEndDate = clickedDate.clone().endOf("day");
    }

    // Switch to daily view
    setStartDate(newStartDate);
    setEndDate(newEndDate);
    setDateType("day");
    setGroupedBy("hourly");
  }, [grouped_by, effectiveDayStartHour]);

  // Helper function to get the appropriate color for a device type based on its usage
  const getColorForUsage = useCallback(
    (typeId, usage) => {
      const deviceType = deviceTypes.find((dt) => dt.id === parseInt(typeId));
      if (!deviceType) return "#26A69A";

      const typeLimits = deviceLimits[typeId] || {};
      const yellowLimit = (typeLimits.yellow_limit || 0) * totalPersons * totalDays;
      const redLimit = (typeLimits.red_limit || 0) * totalPersons * totalDays;

      if (usage > redLimit && redLimit > 0) return theme.palette.red.main;
      if (usage > yellowLimit && yellowLimit > 0) return theme.palette.yellow.main;
      return "#26A69A";
    },
    [deviceTypes, deviceLimits, totalPersons, totalDays, theme]
  );

  const getAlertLevel = useCallback(
    (typeId, usage) => {
      const typeLimits = deviceLimits[typeId] || {};
      const yellowLimit = (typeLimits.yellow_limit || 0) * totalPersons * totalDays;
      const redLimit = (typeLimits.red_limit || 0) * totalPersons * totalDays;

      if (usage > redLimit && redLimit > 0) return "red";
      if (usage > yellowLimit && yellowLimit > 0) return "yellow";
      return null;
    },
    [deviceLimits, totalPersons, totalDays]
  );




  // Fonction pour exporter les données en CSV
  // Fonction pour exporter les données en CSV
  // Fonction pour exporter les données en CSV
  // Fonction pour exporter les données en CSV
  const handleExportCSV = () => {
    if (!usageData || !deviceHistories) return;

    // Préparer les en-têtes du CSV
    const headers = ["Date/Heure"];
    const deviceColumns = [];

    // Collecter tous les devices et leurs types
    Object.entries(deviceHistories).forEach(([typeId, devices]) => {
      const deviceType = deviceTypes.find(dt => dt.id === parseInt(typeId));
      
      // Ignorer le type switch (relais ON/OFF) - pas une consommation
      if (deviceType && deviceType.name.toLowerCase().includes("switch")) {
        return;
      }
      
      Object.entries(devices).forEach(([deviceId, data]) => {
        let columnName = "";
        let columnType = "";
        if (deviceType && deviceType.name.toLowerCase().includes("water")) {
          columnName = "Consommation d'eau (Litres)";
          columnType = "water";
        } else if (deviceType && deviceType.name.toLowerCase().includes("electric")) {
          columnName = "Consommation électrique (kWh)";
          columnType = "electricity";
        } else if (deviceType && (deviceType.name.toLowerCase().includes("temperature") || 
                                  deviceType.name.toLowerCase().includes("humidity") || 
                                  deviceType.name.toLowerCase().includes("co2"))) {
          if (deviceType.name.toLowerCase().includes("humidity")) {
            columnName = "Humidité (%)";
            columnType = "humidity";
          } else if (deviceType.name.toLowerCase().includes("co2")) {
            columnName = "CO2 (ppm)";
            columnType = "co2";
          } else {
            columnName = "Température (°C)";
            columnType = "temperature";
          }
        } else {
          columnName = `${deviceId}`;
          columnType = "other";
        }
        headers.push(columnName);
        deviceColumns.push({ deviceId, typeId, columnName, columnType });
      });
    });

    // Collecter tous les timestamps uniques
    const timestampSet = new Set();
    Object.values(deviceHistories).forEach(devices => {
      Object.values(devices).forEach(data => {
        if (Array.isArray(data)) {
          data.forEach(entry => {
            if (entry.time_created) {
              timestampSet.add(entry.time_created);
            }
          });
        }
      });
    });

    // Trier les timestamps
    const timestamps = Array.from(timestampSet).sort();

    // Initialiser les totaux
    const totals = {};
    deviceColumns.forEach(col => {
      totals[col.deviceId] = 0;
    });

    // Construire les lignes de données
    const rows = timestamps.map(timestamp => {
      const row = [moment(timestamp).format("DD/MM/YYYY HH:mm")];
      
      deviceColumns.forEach(col => {
        const devices = deviceHistories[col.typeId];
        if (devices && devices[col.deviceId] && Array.isArray(devices[col.deviceId])) {
          const entry = devices[col.deviceId].find(d => d.time_created === timestamp);
          if (entry) {
            const value = entry.total_used || entry.temperature || entry.humidity || entry.co2 || 0;
            const formattedValue = parseFloat(value).toFixed(2);
            row.push(formattedValue);
            totals[col.deviceId] += parseFloat(value);
          } else {
            row.push("");
          }
        } else {
          row.push("");
        }
      });
      
      return row;
    });

    // Ajouter la ligne de total
    const totalRow = ["TOTAL"];
    deviceColumns.forEach(col => {
      totalRow.push(totals[col.deviceId].toFixed(2));
    });
    rows.push(totalRow);
    // Ajouter la ligne de coût (uniquement pour eau et électricité)
    const costRow = ["COÛT (€)"];
    
    deviceColumns.forEach(col => {
      const category = deviceTypeCategories[col.typeId];
      let costPerUnit = 0;
      
      if (category && category.cost_per_unit) {
        costPerUnit = category.cost_per_unit;
      } else if (col.columnType === "water" && establishment && establishment.cost_per_liter) {
        costPerUnit = establishment.cost_per_liter; // Diviser par 1000 pour l'eau
      } else if (col.columnType === "electricity" && establishment && establishment.cost_per_kwh) {
        costPerUnit = establishment.cost_per_kwh;
      }
      
      if (costPerUnit > 0 && (col.columnType === "water" || col.columnType === "electricity")) {
        const cost = totals[col.deviceId] * costPerUnit;
        costRow.push(cost.toFixed(2));
      } else {
        costRow.push(""); // Pas de coût pour température ou autres
      }
    });
    rows.push(costRow);


    // Créer le contenu CSV
    const csvContent = [
      headers.map(h => `"${h}"`).join(","),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(","))
    ].join("\n");

    // Créer le blob et télécharger
    const blob = new Blob(["\ufeff" + csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    
    const fileName = `EcoLudik_${residence.name || "Export"}_${moment(startDate).format("YYYYMMDD")}_${moment(endDate).format("YYYYMMDD")}.csv`;
    
    link.setAttribute("href", url);
    link.setAttribute("download", fileName);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {loading && isInitialLoad ? (
        <Grid
          container
          justifyContent="center"
          alignItems="center"
          sx={{ minHeight: "50vh" }}
        >
          <Grid item xs={12} sx={{ textAlign: "center" }}>
            <CircularProgress color="success" size={60} thickness={4} />
            <Typography variant="subtitle1" align="center" sx={{ mt: 2 }}>
              {t("common.loading")}
            </Typography>
          </Grid>
        </Grid>
      ) : error ? (
        <Grid item xs={12}>
          <Typography
            variant="subtitle1"
            align="center"
            color="error"
            sx={{ my: 1 }}
          >
            {t("common.error")}: {error}
          </Typography>
        </Grid>
      ) : (
        // Main content - only rendered when data is loaded
        <>
        <Grid container spacing={1} sx={{ mb: 1 }}>
          {residence && (
            <Grid
              item
              xs={10}
              sx={{ mb: 1, display: "flex", justifyContent: "flex-end", pr: 1, alignItems: "center" }}
              >
                <SelectResidence />
            </Grid>
          )}
          {residence && residence.pack_id && (
            <Grid
              item
              xs={2}
              sx={{ mb: 1, display: "flex", justifyContent: "flex-end" }}
            >
              <Button
                color="success"
                variant="contained"
                onClick={() => navigate('/')}
                sx={{
                  ml: 1,
                  width: "56px",
                  height: "56px",
                  backgroundColor: "#26A69A",
                  color: "black",
                  "&:hover": {
                    backgroundColor: "#00897B",
                  }
                }}
                title={t("common.home") || "Accueil"}
              >
                <Home />
              </Button>
            </Grid>
          )}
        </Grid>
          <Grid container spacing={1}>
          <Grid item xs={12}>
            <Stack gap={1} direction={"row"} flexWrap={"wrap"}>
              <Button
                variant={dateType === "day" ? "contained" : "outlined"}
                onClick={() => changePeriod("day")}
                startIcon={!isMobile && <CalendarViewDay />}
                color="success"
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  px: isMobile ? 0.5 : 1,
                }}
                size="small"
              >
                {t("common.day")}
              </Button>
              <Button
                variant={dateType === "week" ? "contained" : "outlined"}
                onClick={() => changePeriod("week")}
                startIcon={!isMobile && <CalendarViewWeek />}
                color="success"
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  px: isMobile ? 0.5 : 1,
                }}
                size="small"
              >
                {t("common.week")}
              </Button>
              <Button
                variant={dateType === "month" ? "contained" : "outlined"}
                onClick={() => changePeriod("month")}
                startIcon={!isMobile && <CalendarViewMonth />}
                color="success"
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  px: isMobile ? 0.5 : 1,
                }}
              >
                {t("common.month")}
              </Button>
              <Button
                color="success"
                variant="outlined"
                onClick={() => {
                  changePeriod("minus");
                }}
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  px: isMobile ? 0.5 : 1,
                  pl: 0,
                  pr: 0,
                  minWidth: "20px",
                }}
              >
                <ChevronLeft />
              </Button>
              <Button
                startIcon={!isMobile && <CalendarViewDay />}
                color="success"
                variant="outlined"
                onClick={() => setShowDatePicker(!showDatePicker)}
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  px: isMobile ? 0.5 : 1,
                  ...(isMobile && { flex: 1 }),
                }}
                size="small"
              >
                {!isMobile
                  ? moment(startDate).format("DD/MM/YYYY") +
                    " - " +
                    moment(endDate).format("DD/MM/YYYY")
                  : moment(startDate).format("DD/MM") ===
                    moment(endDate).format("DD/MM")
                  ? moment(startDate).format("DD/MM/YY")
                  : moment(startDate).format("DD/MM/YY") +
                    " - " +
                    moment(endDate).format("DD/MM/YY")}
              </Button>
              <Button
                color="success"
                variant="outlined"
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  px: isMobile ? 0.5 : 1,
                  pl: 0,
                  pr: 0,
                  minWidth: "20px",
                }}
                onClick={() => {
                  changePeriod("plus");
                }}
              >
                <ChevronRight />
              </Button>
              {!isMobile && (<Button
                color="success"
                variant="outlined"
                onClick={handleExportCSV}
                startIcon={!isMobile && <Download />}
                sx={{
                  fontSize: isMobile ? "0.7rem" : "0.8rem",
                  px: isMobile ? 0.5 : 1,
                }}
                size="small"
              >
                {t("common.export") || "EXPORTER"}
              </Button>)}
            </Stack>
          </Grid>
        </Grid>

          {/* Summary metrics - show for all device types */}
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {(!deviceTypeCategories ||
              Object.keys(deviceTypeCategories).length === 0) &&
            plugyEuis.size === 0 ? (
              <Grid item xs={12}>
                <Typography variant="subtitle1" align="center" sx={{ my: 1 }}>
                  {t("common.no_utility_devices")}
                </Typography>
              </Grid>
            ) : (
              <>
                {/* Dynamically display metrics for all device types that have cost associated */}
                {(() => {
                  // Calculate visible categories (excluding switches and SDM630MCT triphasé)
                  const visibleCategories = Object.entries(deviceTypeCategories).filter(
                    ([typeId, category]) =>
                      category.devices?.length > 0 &&
                      !category.name?.toLowerCase().includes("switch") &&
                      !(sdm630Config && sdm630Config.allTypeIds.has(typeId))
                  );
                  const visibleCount = visibleCategories.length;

                  return Object.entries(deviceTypeCategories).flatMap(
  ([typeId, category]) => {
    // Skip if we don't have devices or usage data for this type
    if (!category.devices || !category.devices.length)
      return [];

    // Skip switch devices (relay ON/OFF) - not a consumption metric
    if (category.name?.toLowerCase().includes("switch"))
      return [];

    // Skip SDM630MCT triphasé devices - rendered in dedicated section below
    if (sdm630Config && sdm630Config.allTypeIds.has(typeId))
      return [];
    
    // For temperature devices, create one card per device
if (category.name?.toLowerCase().includes("temp")) {
  return category.devices.map((deviceId) => {
    const device = devicesByType[typeId]?.find(d => d.dev_eui === deviceId) || { name: deviceId };
    const histories = deviceHistories[typeId]?.[deviceId] || [];
    
    // AM103 specific logic
    if (device.name && device.name.includes("AM103")) {
      if (device.name.includes("_CH3")) {
        // AM103 Humidity
        let lastHumidity = 0;
        for (let i = histories.length - 1; i >= 0; i--) {
          if (histories[i].humidity !== undefined && histories[i].humidity !== 0) {
            lastHumidity = histories[i].humidity;
            break;
          }
        }
        // Arrondir à 2 décimales
        lastHumidity = Math.round(lastHumidity * 100) / 100;
        // Calculer la taille de grille : 12 pour 1, 6 pour 2, 4 pour 3+
        const gridSize = category.devices.length === 1 ? 12 : category.devices.length === 2 ? 6 : 4;
        return (
          <Grid item xs={12} sm={6} md={gridSize} key={deviceId}>
            <UsageMetric
              title="Humidité"
              value={lastHumidity}
              unit="%"
              icon={<DeviceTypeIcon deviceType="humidity" deviceBrandId={38} size={60} />}
              color="cyan"
            />
          </Grid>
        );
      } else if (device.name.includes("_CH4")) {
        // AM103 CO2
        let lastCO2 = 0;
        for (let i = histories.length - 1; i >= 0; i--) {
          if (histories[i].co2 !== undefined && histories[i].co2 !== 0) {
            lastCO2 = histories[i].co2;
            break;
          }
        }
        // Arrondir à 2 décimales
        lastCO2 = Math.round(lastCO2 * 100) / 100;
        // Calculer la taille de grille : 12 pour 1, 6 pour 2, 4 pour 3+
        const gridSize = category.devices.length === 1 ? 12 : category.devices.length === 2 ? 6 : 4;
        return (
          <Grid item xs={12} sm={6} md={gridSize} key={deviceId}>
            <UsageMetric
              title="CO2"
              value={lastCO2}
              unit="ppm"
              icon={<DeviceTypeIcon deviceType="co2" deviceBrandId={39} size={60} />}
              color="orange"
            />
          </Grid>
        );
      }
    }

    // Original temperature logic
    let lastTemp = 0;
    for (let i = histories.length - 1; i >= 0; i--) {
      if (histories[i].temperature !== undefined && histories[i].temperature !== 0) {
        lastTemp = histories[i].temperature;
        break;
      }
    }
    // Arrondir à 2 décimales pour éviter les longues décimales
    lastTemp = Math.round(lastTemp * 100) / 100;

    // Calculer la taille de grille : 12 pour 1, 6 pour 2, 4 pour 3+
    const gridSize = category.devices.length === 1 ? 12 : category.devices.length === 2 ? 6 : 4;
    return (
      <Grid item xs={12} sm={6} md={gridSize} key={deviceId}>
        <UsageMetric
          title={getTemperatureTypeLabel(deviceTemperatureTypes[device.dev_eui]) || t("common.temperature") || "Température"}
          value={lastTemp}
          unit="°C"
          icon={<DeviceTypeIcon deviceType={category.name} deviceBrandId={device.name?.includes("AM103") ? 37 : undefined} size={60} />}
          isTemperature={true}
          alertColor={getTemperatureLevel(lastTemp || null, deviceTempThresholds[device.dev_eui]) || alertColors.deviceColors?.[`temperature_${device.dev_eui}`] || alertColors.deviceColors?.temperature || alertColors.temperature}
        />
      </Grid>
    );
  });
}    

    // For UC300 devices, render channels in pairs (CH1+CH2, then CH3+CH4):
    //   row 1: card1 | card2
    //   row 2: graph1 | graph2
    //   row 3: card3 | card4
    //   row 4: graph3 | graph4
    // If the pair has only one channel (e.g. 1 or 3 channels total), card + graph
    // are rendered full-width and stacked.
    if (category.name?.toLowerCase().includes("uc300") ||
        (category.devices && category.devices.some(deviceId => {
          const device = devicesByType[typeId]?.find(d => d.dev_eui === deviceId);
          return device && device.name?.toLowerCase().includes("uc300");
        }))) {

      const uc300Devices = category.devices.map((deviceId) => {
        const device = devicesByType[typeId]?.find(d => d.dev_eui === deviceId) || { name: deviceId };
        return { deviceId, device };
      }).sort((a, b) => {
        const getChannelNum = (name) => {
          if (name?.includes("_CH1")) return 1;
          if (name?.includes("_CH2")) return 2;
          if (name?.includes("_CH3")) return 3;
          if (name?.includes("_CH4")) return 4;
          return 0;
        };
        return getChannelNum(a.device.name) - getChannelNum(b.device.name);
      });

      const elements = [];

      for (let i = 0; i < uc300Devices.length; i += 2) {
        const pair = [uc300Devices[i], uc300Devices[i + 1]].filter(Boolean);
        const isSingleton = pair.length === 1;
        const cardMd = isSingleton ? 12 : 6;
        const cardXs = isSingleton ? 12 : 6;
        const graphMd = isSingleton ? 12 : 6;

        // Row of cards (1 or 2)
        pair.forEach(({ deviceId, device }) => {
          let deviceTotal = 0;
          const deviceHistory = deviceHistories[typeId]?.[deviceId] || [];
          if (deviceHistory.length > 0) {
            deviceTotal = deviceHistory.reduce((sum, entry) => {
              const value = entry[category.field] || 0;
              return sum + value;
            }, 0);
          }

          const costPerUnit = (category.cost_per_unit ? (category.cost_per_unit / (category.cost_divisor || 1)) : null) || establishment.cost_per_kwh || 0;
          const calculatedCost = Math.round(costPerUnit * deviceTotal * 100) / 100;
          const secondaryValue = (costPerUnit > 0 && !isNaN(calculatedCost)) ? calculatedCost + "€" : "";

          let channelName = "UC300";
          let channelNum = null;
          if (device.name?.includes("_CH1")) { channelName = "RECHARGE TPC1"; channelNum = 1; }
          else if (device.name?.includes("_CH2")) { channelName = "RECHARGE TPC2"; channelNum = 2; }
          else if (device.name?.includes("_CH3")) { channelName = "RECHARGE TPC3"; channelNum = 3; }
          else if (device.name?.includes("_CH4")) { channelName = "RECHARGE TPC4"; channelNum = 4; }

          // Toggle ADE_DRY si un canal ADE_DRY de même numéro existe dans le pack
          const adeDryEntry = channelNum ? adeDryByChannel[channelNum] : null;
          const relayControl = adeDryEntry
            ? {
                state: adeDryStates[channelNum] !== undefined
                  ? adeDryStates[channelNum]
                  : adeDryEntry.currentState,
                onToggle: (newState) => handleAdeDryToggle(channelNum, newState),
              }
            : null;

          elements.push(
            <Grid item xs={cardXs} md={cardMd} key={`card-${deviceId}`}>
              <UsageMetric
                title={channelName}
                value={Math.round(deviceTotal * 100) / 100}
                unit="kWh"
                secondaryValue={secondaryValue}
                icon={<EvStation sx={{ fontSize: 60, color: "#ffa726" }} />}
                color="orange"
                alertColor={getAlertLevel(typeId, deviceTotal)}
                relayControl={relayControl}
              />
            </Grid>
          );
        });

        // Row of graphs (matching the cards above)
        pair.forEach(({ deviceId, device }) => {
          const data = deviceHistories[typeId]?.[deviceId] || [];
          elements.push(
            <Grid item xs={12} md={graphMd} key={`graph-${deviceId}`}>
              <DeviceGraph
                title={`${t(`common.electric_meter`) || "electric"} `}
                subtitle={`${device.name || deviceId}`}
                data={data}
                value={category.field || ""}
                aspect={isMobile ? 2 : (isSingleton ? 5 : 3)}
                groupedBy={grouped_by}
                unit={translateUnit(category.unit_name) || ""}
                typeName="electric"
                chartType="bar"
                accumulate={true}
                onBarClick={grouped_by === "daily" ? handleBarClick : undefined}
              />
            </Grid>
          );
        });
      }

      return elements;
    }
                    // Get total for this device type from deviceTotals
const totalForPeriod = (() => {
  if (category.name?.toLowerCase().includes("temp")) {
    const histories = deviceHistories[typeId] || {};
    for (const deviceId in histories) {
      const data = histories[deviceId] || [];
      for (let i = data.length - 1; i >= 0; i--) {
        if (data[i].temperature !== undefined && data[i].temperature !== 0) {
          return data[i].temperature;
        }
      }
    }
    return 0;
  }
  return deviceTotals[typeId] || 0;
})();

                    // Detect vanne thermo / TRV
                    const isVanneThermo = category.name?.toLowerCase().includes("vanne") ||
                                          category.name?.toLowerCase().includes("thermo") ||
                                          category.name?.toLowerCase().includes("trv");

                    // Get appropriate icon based on device type name
                    let icon = isVanneThermo ? (
                      <Whatshot sx={{ fontSize: isMobile ? 50 : 80, color: "#F97316" }} />
                    ) : (
                      <DeviceTypeIcon
                        deviceType={category.name}
                        color={getColorForUsage(typeId, totalForPeriod)}
                        size={isMobile ? 50 : 80}
                      />
                    );

                    // Calculate cost if applicable
logger.log("DEBUG - category data:", category);
logger.log("DEBUG - cost_per_unit:", category.cost_per_unit);
logger.log("DEBUG - cost_divisor:", category.cost_divisor);
                    let secondaryValue = "";
                    const costPerUnit = (category.cost_per_unit ? (category.cost_per_unit / (category.cost_divisor || 1)) : null) ||
                      (category.name?.toLowerCase().includes("water")
                        ? establishment.cost_per_liter
                        : 0) ||
                      (category.name?.toLowerCase().includes("electric")
                        ? establishment.cost_per_kwh
                        : 0);
                    const calculatedValue =
                      Math.round(costPerUnit * totalForPeriod * 100) / 100;
                    // Only show cost if costPerUnit > 0, otherwise show empty string
                    secondaryValue = (costPerUnit > 0 && !isNaN(calculatedValue))
                      ? calculatedValue + "€"
                      : "";

                    // Determine unit name
                    const unitName = category.unit_name || "";

                    return (
                      <Grid
                        item
                        xs={
                          visibleCount > 1 ? 6 : 12
                        }
                        md={
                          visibleCount > 1 ? 6 : 12
                        }
                        key={typeId}
                      >
                        <UsageMetric
                          title={
                            isVanneThermo
                              ? "Vanne Thermo"
                              : t(`common.${category.name?.toLowerCase()}_${unitName?.toLowerCase()}`) || category.name
                          }
                          value={totalForPeriod}
                          unit={translateUnit(unitName)}
                          color={getColorForUsage(typeId, totalForPeriod)}
                          secondaryValue={secondaryValue}
                          icon={icon}
                          yesterdayValue={yesterdayTotals[typeId] || 0}
                          showComparison={category.name?.toLowerCase().includes("water") || category.name?.toLowerCase().includes("electric")}
                          alertColor={
                            (category.name?.toLowerCase().includes("water") || category.name?.toLowerCase().includes("electric"))
                              ? getAlertLevel(typeId, totalForPeriod)
                              : category.name?.toLowerCase().includes("temp") ? alertColors.temperature : null
                          }
                          isVanneThermo={isVanneThermo}
                        />
                      </Grid>
                    );
                  }
                );
                })()}

                {/* ============================================= */}
                {/* SDM630MCT Triphasé Section                    */}
                {/* ============================================= */}
                {sdm630Config && (() => {
                  const { parentDevice, parentTypeId, phases, phaseTypeId } = sdm630Config;
                  const elements = [];

                  // --- 1. Carte principale : Consommation totale kWh (pleine largeur) ---
                  let totalKwh = 0;
                  if (parentTypeId) {
                    totalKwh = deviceTotals[parentTypeId] || 0;
                  }
                  const parentCategory = deviceTypeCategories[parentTypeId] || {};
                  const costPerUnit = (parentCategory.cost_per_unit
                    ? (parentCategory.cost_per_unit / (parentCategory.cost_divisor || 1))
                    : null) || establishment.cost_per_kwh || 0;
                  const calculatedCost = Math.round(costPerUnit * totalKwh * 100) / 100;
                  const totalSecondary = (costPerUnit > 0 && !isNaN(calculatedCost))
                    ? calculatedCost + "€" : "";

                  elements.push(
                    <Grid item xs={12} key="sdm630-total">
                      <UsageMetric
                        title="Électricité Triphasé"
                        value={Math.round(totalKwh * 100) / 100}
                        unit="kWh"
                        secondaryValue={totalSecondary}
                        icon={<Bolt sx={{ fontSize: isMobile ? 50 : 80, color: "#ffd54f" }} />}
                        showComparison={true}
                        yesterdayValue={yesterdayTotals[parentTypeId] || 0}
                        alertColor={getAlertLevel(parentTypeId, totalKwh)}
                      />
                    </Grid>
                  );

                  // --- 2. Cartes par phase (3 colonnes) ---
                  const phaseConfig = [
                    { key: "l1", label: "Phase L1", color: "#FF6B6B" },
                    { key: "l2", label: "Phase L2", color: "#4ECDC4" },
                    { key: "l3", label: "Phase L3", color: "#FFD93D" },
                  ];

                  phaseConfig.forEach(({ key, label, color }) => {
                    const phaseData = phases[key];
                    if (!phaseData) return;

                    const histories = deviceHistories[phaseData.typeId]?.[phaseData.deviceId] || [];

                    // Récupérer les dernières valeurs instantanées (last_power = puissance temps réel, power = moyenne horaire pour le graphe)
                    let lastPower = 0;
                    let lastVoltage = null;
                    let lastCurrent = null;
                    for (let i = histories.length - 1; i >= 0; i--) {
                      const entry = histories[i];
                      // Utiliser last_power (instantané) pour les cartes, sinon fallback sur power
                      const instantPower = entry.last_power !== undefined ? entry.last_power : entry.power;
                      if (instantPower !== undefined && instantPower !== 0) {
                        lastPower = Math.round(instantPower * 100) / 100;
                        lastVoltage = entry.voltage !== undefined
                          ? Math.round(entry.voltage * 10) / 10 : null;
                        // last_current = instantané (cartes) ; current = série par fenêtre (graphe)
                        const instantCurrent = entry.last_current !== undefined ? entry.last_current : entry.current;
                        lastCurrent = instantCurrent !== undefined
                          ? Math.round(instantCurrent * 1000) / 1000 : null;
                        break;
                      }
                    }

                    elements.push(
                      <Grid item xs={4} key={`sdm630-${key}`}>
                        <UsageMetric
                          title={label}
                          value={lastPower}
                          unit="W"
                          icon={<Bolt sx={{ fontSize: isMobile ? 30 : 50, color: color }} />}
                          phaseInfo={{
                            voltage: lastVoltage,
                            current: lastCurrent,
                            color: color,
                          }}
                        />
                      </Grid>
                    );
                  });

                  // --- 3. Graphe consommation kWh (parent device) ---
                  if (parentDevice && parentTypeId) {
                    const parentHistory = deviceHistories[parentTypeId]?.[parentDevice.dev_eui] || [];
                    const parentField = parentCategory.field || "total_used";
                    const parentUnit = translateUnit(parentCategory.unit_name) || "kWh";

                    elements.push(
                      <Grid item xs={12} key="sdm630-kwh-graph">
                        <DeviceGraph
                          title="Consommation (kWh)"
                          subtitle={parentDevice.name || parentDevice.dev_eui}
                          data={parentHistory}
                          value={parentField}
                          aspect={isMobile ? 2.5 : 4.5}
                          groupedBy={grouped_by}
                          unit={parentUnit}
                          typeName="electric"
                          chartType="bar"
                          accumulate={true}
                          onBarClick={grouped_by === "daily" ? handleBarClick : undefined}
                        />
                      </Grid>
                    );
                  }

                  // --- 4. Graphe barres groupées triphasé ---
                  const phaseHistories = [
                    deviceHistories[phases.l1?.typeId]?.[phases.l1?.deviceId] || [],
                    deviceHistories[phases.l2?.typeId]?.[phases.l2?.deviceId] || [],
                    deviceHistories[phases.l3?.typeId]?.[phases.l3?.deviceId] || [],
                  ];

                  elements.push(
                    <Grid item xs={12} key="sdm630-graph">
                      <TriphaseGraph
                        phaseHistories={phaseHistories}
                        fieldName="power"
                        groupedBy={grouped_by}
                        aspect={isMobile ? 2.5 : 4.5}
                        onBarClick={grouped_by === "daily" ? handleBarClick : undefined}
                      />
                    </Grid>
                  );

                  return elements;
                })()}
              </>
            )}
          </Grid>

          {/* Bornes de recharge Plugy (IRVE) — section dédiée (cartes + accordéon) */}
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            <Grid item xs={12}>
              <PlugyChargingStations
                devicesByType={(usageData && usageData.devices_by_type) || {}}
                deviceHistories={deviceHistories}
                groupedBy={grouped_by}
              />
            </Grid>
          </Grid>

          {/* Device Graphs Section */}
          {/* All device graphs in a single container */}
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {(() => {
              // Calculate visible graph categories (excluding switches, UC300, and SDM630MCT)
              // UC300 graphs are rendered alongside their cards in the metrics section above.
              const visibleGraphCategories = Object.entries(deviceTypeCategories).filter(
                ([typeId, category]) => {
                  if (!category.devices?.length) return false;
                  if (category.name?.toLowerCase().includes("switch")) return false;
                  if (category.name?.toLowerCase().includes("uc300")) return false;
                  if (category.devices.some(deviceId => {
                    const device = devicesByType[typeId]?.find(d => d.dev_eui === deviceId);
                    return device && device.name?.toLowerCase().includes("uc300");
                  })) return false;
                  if (sdm630Config && sdm630Config.allTypeIds.has(typeId)) return false;
                  return true;
                }
              );
              const visibleGraphCount = visibleGraphCategories.reduce((sum, [, cat]) => sum + (cat.devices?.length || 0), 0);

              return Object.entries(deviceTypeCategories).flatMap(
              ([typeId, category]) => {
                // Skip if we don't have devices for this type
                if (!category.devices || !category.devices.length) return [];
                // Skip SDM630MCT triphasé devices - already rendered in dedicated section
                if (sdm630Config && sdm630Config.allTypeIds.has(typeId)) return [];
                // Skip UC300 devices - graphs are already rendered with their cards above
                if (category.name?.toLowerCase().includes("uc300") ||
                    (category.devices && category.devices.some(deviceId => {
                      const device = devicesByType[typeId]?.find(d => d.dev_eui === deviceId);
                      return device && device.name?.toLowerCase().includes("uc300");
                    }))) return [];
                // Skip switch devices (relay ON/OFF) - not a consumption metric
                if (category.name?.toLowerCase().includes("switch")) return [];
                return category.devices.map((deviceId, index) => {
                  // Find device data from the devices list
                  const device = devicesByType[typeId]?.find(
                    (d) => d.dev_eui === deviceId
                  ) || {
                    dev_eui: deviceId,
                    name: deviceId,
                  };

                  const data = deviceHistories[typeId]?.[deviceId] || [];
                  const baseName = category.name?.toLowerCase() || "device";

                  // Pour AM103, déterminer le vrai type selon le canal
                  const typeName = device.name?.includes("AM103")
                    ? (device.name?.includes("_CH3") ? "humidity" :
                       device.name?.includes("_CH4") ? "co2" : baseName)
                    : baseName;

                  // Déterminer l'unité correcte selon le type effectif
                  const effectiveUnit = typeName === "humidity" ? "%" :
                                        typeName === "co2" ? "ppm" :
                                        translateUnit(category.unit_name) || "";

                  logger.log(
                    `Rendering graph for ${typeName} (${deviceId}) with data:`,
                    data
                  );

                  // Seuils journaliers pour graphes eau/élec (per_person * nb_personnes)
                  const isWaterOrElec = typeName?.toLowerCase().includes("water") || typeName?.toLowerCase().includes("electric");
                  const typeLimitsForGraph = isWaterOrElec ? (deviceLimits[typeId] || {}) : {};
                  const dailyThresholds = isWaterOrElec ? {
                    yellow: typeLimitsForGraph.yellow_limit ? Math.round(typeLimitsForGraph.yellow_limit * totalPersons) : null,
                    red: typeLimitsForGraph.red_limit ? Math.round(typeLimitsForGraph.red_limit * totalPersons) : null,
                  } : null;

                  // Pour les graphes de température : seuils jaune/rouge par capteur
                  // (lignes de référence + points de dépassement colorés).
                  const isTempGraph = typeName?.toLowerCase().includes("temp");
                  const graphThresholds = isTempGraph
                    ? (deviceTempThresholds[device.dev_eui] || null)
                    : dailyThresholds;

                  // Calculate how many devices we have total across all categories
                  // Using visibleGraphCount calculated above
                  const totalDevices = visibleGraphCount;

                  // Determine grid width based on total number of devices
                  let gridWidth;
                  if (totalDevices === 1) {
                    gridWidth = 12; // 1 chart: full width
                  } else if (totalDevices === 2) {
                    gridWidth = 6; // 2 charts: half width each
                  } else if (totalDevices === 3) {
                    gridWidth = 4; // 3 charts: third width each (3 per row)
                  } else {
                    gridWidth = 6; // 4+ charts: half width each (2 per row)
                  }

                  // Determine lg breakpoint sizing
                  let lgSize;
                  if (totalDevices === 4) {
                    lgSize = 6; // 4 charts: 2 per row on large screens
                  } else if (totalDevices > 3) {
                    lgSize = 4; // 5+ charts: 3 per row on large screens
                  } else {
                    lgSize = gridWidth; // Use same as md for 1-3 charts
                  }

                  return (
                    <Grid
                      item
                      xs={12}
                      
                      md={visibleGraphCount > 1 ? 6 : 12}
                      lg={lgSize}
                      key={`${typeId}-${deviceId}`}
                    >
                      <DeviceGraph
                        title={
                          // Titre spécial pour vanne thermo / TRV
                          typeName?.toLowerCase().includes("vanne") || typeName?.toLowerCase().includes("thermo") || typeName?.toLowerCase().includes("trv")
                            ? "Vanne Thermo"
                            : `${t(`common.${typeName}_meter`) || typeName} `
                        }
                        subtitle={`${device.name || deviceId}`}
                        data={data}
                        value={category.field || ""}
                        aspect={isMobile ? 2 : (visibleGraphCount === 1 ? 5 : 3)}
                        groupedBy={grouped_by}
                        unit={effectiveUnit}
                        typeName={typeName}
                        chartType={
                          category.aggregation_method === "latest" || typeName?.toLowerCase().includes("temp")
                            ? "area"
                            : "bar"
                        }
                        accumulate={category.aggregation_method !== "latest"}
                        temperatureLabel={
                          // Ne pas afficher le label température pour AM103 CH3 (humidity) et CH4 (CO2)
                          (device.name?.includes("AM103") && (device.name?.includes("_CH3") || device.name?.includes("_CH4")))
                            ? null
                            : typeName?.toLowerCase().includes("temp")
                              ? (device.name?.toLowerCase().includes("négative") || device.name?.toLowerCase().includes("negative"))
                                ? "Négative"
                                : (device.name?.toLowerCase().includes("positive"))
                                  ? "Positive"
                                  : (deviceTemperatureTypes[device.dev_eui]?.includes("négative") || deviceTemperatureTypes[device.dev_eui]?.includes("negative"))
                                    ? "Négative"
                                    : deviceTemperatureTypes[device.dev_eui]?.includes("positive")
                                      ? "Positive"
                                      : null
                              : null
                        }
                        onBarClick={grouped_by === "daily" && category.aggregation_method !== "latest" ? handleBarClick : undefined}
                        thresholds={graphThresholds}
                      />
                    </Grid>
                  );
                });
              }
            );
            })()}
          </Grid>

          {/* Occupancy Information - Only show if screen exists */}
          {Object.entries(devicesByType).some(([typeId, devices]) => 
            devices?.some(device => device.name?.toLowerCase().includes("screen"))
          ) && (

          <UsageMetric
            title={t("common.current_occupancy")}
            value={residence ? residence.current_occupation : 0}
            unit={"of " + (residence ? residence.capacity : 0)}
            icon={<People sx={{ fontSize: 80 }} />}
          />
          )}

          {logger.log("DEBUG: residence object:", residence)}
          {logger.log("DEBUG: sms_alerts_enabled value:", residence?.sms_alerts_enabled)}
          <Grid container spacing={1} sx={{ mt: 0.5 }}>
            {Object.keys(devicesByType).length === 0 && plugyEuis.size === 0 ? (
              <Grid item xs={12}>
                <Typography variant="subtitle1" align="center" sx={{ my: 1 }}>
                  {t("common.no_device_data")}
                </Typography>
              </Grid>
            ) : (
              (() => {
                // Filter devices: exclude switch types and SDM630 channel devices (keep parent only)
                const filteredByType = Object.entries(devicesByType).map(([typeId, devs]) => {
                  const deviceType = deviceTypes.find(dt => dt.id === parseInt(typeId));
                  if (deviceType && deviceType.name?.toLowerCase().includes("switch")) return [typeId, []];
                  // In triphasé mode, skip virtual channel devices (CH1/CH2/CH3)
                  const filtered = sdm630Config
                    ? devs.filter(d => !d.dev_eui?.includes("_CH"))
                    : devs;
                  return [typeId, filtered];
                });

                // Pour les devices multicanal (UC300, ADE_DRY, AM103…), n'afficher qu'une
                // seule carte « dernière remontée » par device physique : on dédoublonne
                // les canaux virtuels _CHx en gardant le parent + le timestamp le plus
                // récent parmi tous ses canaux.
                const dedupedByType = filteredByType.map(([typeId, devs]) => {
                  const seenParents = new Set();
                  const result = [];
                  for (const device of devs) {
                    if (device.dev_eui?.includes("_CH")) {
                      const parentDevEui = device.dev_eui.split("_CH")[0];
                      if (seenParents.has(parentDevEui)) continue;
                      seenParents.add(parentDevEui);
                      const siblings = devs.filter(
                        (d) => d.dev_eui?.split("_CH")[0] === parentDevEui
                      );
                      const latest = siblings.reduce((max, d) => {
                        if (!d.time_updated) return max;
                        if (!max || d.time_updated > max) return d.time_updated;
                        return max;
                      }, null);
                      const parentName = device.name?.split("_CH")[0] || parentDevEui;
                      result.push({
                        ...device,
                        dev_eui: parentDevEui,
                        name: parentName,
                        time_updated: latest,
                      });
                    } else {
                      result.push(device);
                    }
                  }
                  return [typeId, result];
                });

                const totalDevices = dedupedByType.reduce((sum, [, devs]) => sum + devs.length, 0);
                // Calculer la taille de grille : 12 pour 1, 6 pour 2, 4 pour 3+
                const gridSize = totalDevices === 1 ? 12 : totalDevices === 2 ? 6 : 4;
                const smSize = totalDevices === 1 ? 12 : 6;
                return dedupedByType.flatMap(([, devices]) => {
                  if (!devices || devices.length === 0) return [];
                  return devices.map((device) => (
                    <Grid item xs={12} sm={smSize} md={gridSize} key={device.dev_eui}>
                      <LastCheckin
                        title={`${device.name || device.dev_eui}`}
                        value={device.time_updated || "N/A"}
                      />
                    </Grid>
                  ));
                });
              })()
            )}
          </Grid>

          {/* SMS Alerts Management - Show if establishment has SMS configured */}
          {smsAllowed && devicesByType && (
            <SmsAlertsCard
              devicesByType={devicesByType}
              deviceSmsStates={deviceSmsStates}
              deviceTemperatureTypes={deviceTemperatureTypes}
              deviceThresholds={deviceThresholds}
              devicePauseStates={devicePauseStates}
              onSmsToggle={handleDeviceSmsToggle}
              onSmsPause={handleDeviceSmsPause}
              onThresholdChange={handleThresholdChange}
              isMobile={isMobile}
            />
          )}

          {/* Alert Thresholds Section */}
          {residence && establishment && (hasWaterDevices || hasElecDevices) && (
            <AlertThresholdsSection
              residence={residence}
              establishment={establishment}
              residenceId={id}
              hasWater={hasWaterDevices}
              hasElec={hasElecDevices}
              isMobile={isMobile}
            />
          )}

          {/* Seuils de coloration température (jaune/rouge), indépendants du SMS */}
          {residence && temperatureDevicesList.length > 0 && (
            <TemperatureThresholdsSection
              temperatureDevices={temperatureDevicesList}
              thresholds={deviceTempThresholds}
              onSaved={(devEui, vals) => {
                setDeviceTempThresholds((prev) => ({
                  ...prev,
                  [devEui]: { ...prev[devEui], ...vals },
                }));
              }}
              isMobile={isMobile}
            />
          )}

          {/* Étalonnage des sondes : replié par défaut, et distinct des seuils.
              Régler la justesse de la mesure (une fois) et régler quand alerter
              (ajusté régulièrement) sont deux gestes de nature différente. */}
          {residence && temperatureDevicesList.length > 0 && (
            <CalibrationSection
              temperatureDevices={temperatureDevicesList}
              isMobile={isMobile}
              onChanged={() => setCalibrationVersion((v) => v + 1)}
            />
          )}

          {/* Index Readings Section - uniquement pour devices ADE_PULSE */}
          {devicesByType && Object.keys(devicesByType).length > 0 && (
            <IndexReadingsSection
              devicesByType={devicesByType}
              indexReadings={indexReadings}
              onAddReading={(devEui) => {
                setSelectedDeviceForReading(devEui);
                setShowAddIndexReadingDialog(true);
              }}
              onEditReading={(devEui, reading) => {
                setSelectedDeviceForReading(devEui);
                setSelectedReadingToEdit(reading);
                setShowEditIndexReadingDialog(true);
              }}
              onDeviceUpdate={(devEui, updates) => {
                // Le pulse_multiplier est persisté côté backend
                logger.log(`Device ${devEui} updated:`, updates);
              }}
              isMobile={isMobile}
            />
          )}

        </>
      )}

      {/* Add Index Reading Dialog */}
      {selectedDeviceForReading && (
        <AddIndexReadingDialog
          open={showAddIndexReadingDialog}
          onClose={() => {
            setShowAddIndexReadingDialog(false);
            setSelectedDeviceForReading(null);
          }}
          dev_eui={selectedDeviceForReading}
          onSuccess={() => {
            // Refresh index readings after successful creation
            fetchIndexReadings(selectedDeviceForReading, 100);
          }}
        />
      )}

      {/* Edit Index Reading Dialog */}
      {selectedDeviceForReading && selectedReadingToEdit && (
        <EditIndexReadingDialog
          open={showEditIndexReadingDialog}
          onClose={() => {
            setShowEditIndexReadingDialog(false);
            setSelectedDeviceForReading(null);
            setSelectedReadingToEdit(null);
          }}
          dev_eui={selectedDeviceForReading}
          reading={selectedReadingToEdit}
          onSuccess={() => {
            // Refresh index readings after successful update
            fetchIndexReadings(selectedDeviceForReading, 100);
          }}
        />
      )}

      <DatePickerDialog
        open={showDatePicker}
        setOpen={setShowDatePicker}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        onClose={() => setShowDatePicker(false)}
        setGroupedBy={setGroupedBy}
      />
    </>
  );
}

export default withAuthenticationRequired(ResidenceUsage);
